import {Visibility} from './Visibility';
import {ViewMode} from './ViewMode';

export enum MessageToParentType {
  // Signal to the parent that the widget iframe is initialized
  //
  // This allows the parent to know when the iframe is ready to
  // receive messages and thus is safe to open.
  SetWidgetInitialized = 'widget-initialized',

  // Requests an action be performed by the parent
  //
  // These actions could be fire-and-forget, such as requesting
  // a modal is opened, or can return a response, such as
  // requesting the parent's page context
  RequestAction = 'request-action',

  // Requests the parent to change the visibility of the widget.
  //
  // Since the parent owns the iframe, it has the authority on the
  // visibility state. When the iframe wants to change the visibility, it
  // sends that request to the parent. Once the parent has handle the
  // request, it sends a message to the iframe with the new visibility.
  RequestVisibilityChange = 'request-visibility-change',

  // Requests the parent to change the view mode of the widget.
  //
  // If the experience within the iframe is changing, e.g. from the normal
  // widget experience to a feedback form, the iframe can request this view
  // mode from the parent. This allows the parent to handle the change in
  // view mode appropriately, adjusting any styling or sizing necessary.
  RequestViewMode = 'request-view-mode',

  // Requests the latest analytics event ID from the parent.
  //
  // We use the analytics event ID when creating cases so that we can link cases
  // to user sessions in analytics. This is used to calculate, among other
  // things, self serve escalation rates or SSER, a critical metric for self
  // serve support.
  RequestAnalyticsEventId = 'request-analytics-event-id',

  // Informs the parent of the height of the header.
  //
  // This allows the parent frame to know the proper height of the header for
  // minimizing, since the parent has to align the frame to the bottom of the
  // viewport.
  SetHeaderHeight = 'set-header-height',

  // Updates the parent with the status of unseen messages.
  //
  // This allows the parent frame to know how to change its UI, such as by
  // changing its favicon to show that there are new notifications.
  SetUnseenMessagesCount = 'set-unseen-messages-count',

  // Requests the parent window to navigate to a URL.
  //
  // This allows the iframe to request navigation in a safe manner, where the
  // parent window handles the redirection using the safe-links utility to
  // validate the URL before redirecting.
  RequestUrlRedirect = 'request-url-redirect',

  // DEPRECATED:

  // Requests the parent to open the email modal.
  //
  // This should only be used when the widget is iframed in the dashboard,
  // since we want to open the native dashboard email modal instead of
  // redirection to a url.
  RequestOpenEmailModal = 'request-open-email-modal',

  // Updates the parent with the preferred size for the widget.
  //
  // This message allows the widget itself to provide the preferred size for the
  // frame, allowing control for both static sizing as well as enabling the
  // ability to dynamically resize the frame if that's something we want.
  // Putting this control in the widget means we can update sizes in the client
  // and know that all frames on all surfaces will show the appropriate height.
  SetPreferredSize = 'set-preferred-size',
}

export interface BaseMessageToParent {
  audience?: string;
  entryPoint?: string;
}

export interface RequestVisibilityChangeMessage extends BaseMessageToParent {
  type: MessageToParentType.RequestVisibilityChange;
  data: {
    requestedVisibility: Visibility;
  };
}

export interface RequestViewModeMessage extends BaseMessageToParent {
  type: MessageToParentType.RequestViewMode;
  data: {
    viewMode: ViewMode;
  };
}

export interface SetPreferredSizeMessage extends BaseMessageToParent {
  type: MessageToParentType.SetPreferredSize;
  data: {
    width: number;
    height: number;
  };
}

export interface SetHeaderHeightMessage extends BaseMessageToParent {
  type: MessageToParentType.SetHeaderHeight;
  data: {
    headerHeight: number;
  };
}

export interface RequestAnalyticsEventIdMessage extends BaseMessageToParent {
  type: MessageToParentType.RequestAnalyticsEventId;
}

export interface SetUnseenMessagesCountMessage extends BaseMessageToParent {
  type: MessageToParentType.SetUnseenMessagesCount;
  data: {
    unseenMessagesCount: number;
  };
}

export interface RequestOpenEmailModalMessage extends BaseMessageToParent {
  type: MessageToParentType.RequestOpenEmailModal;
  data: {
    topic?: string;
  };
}

export interface RequestAction extends BaseMessageToParent {
  type: MessageToParentType.RequestAction;
  data: {
    name: string;
    data?: Record<string, unknown>;
  };
  nonce?: string;
}

export interface SetWidgetInitializedMessage extends BaseMessageToParent {
  type: MessageToParentType.SetWidgetInitialized;
}

export interface RequestUrlRedirectMessage extends BaseMessageToParent {
  type: MessageToParentType.RequestUrlRedirect;
  data: {
    url: string;
  };
}

export type MessageToParent =
  | RequestVisibilityChangeMessage
  | RequestViewModeMessage
  | SetPreferredSizeMessage
  | SetHeaderHeightMessage
  | RequestAnalyticsEventIdMessage
  | SetUnseenMessagesCountMessage
  | RequestOpenEmailModalMessage
  | RequestUrlRedirectMessage
  | RequestAction
  | SetWidgetInitializedMessage;
